import { ArrowDownTrayIcon, ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/24/solid';
import { Link } from '@remix-run/react';
import { capitaliseAndSpace, cn } from '@theprintbar/shared';
import type React from 'react';
import { createContext, useContext, useState } from 'react';
import { getTableSearchURL } from '../../tables/PaginatedTable';
import { PatternImage } from '../PatternImage';
import { StatusIcon, StatusIconStatus } from '../StatusIcon';

const TableContext = createContext<{ bleed: boolean; dense: boolean; grid: boolean; striped: boolean; noBorder: boolean }>({
	bleed: false,
	dense: false,
	grid: false,
	striped: false,
	noBorder: false,
});

export function Table({
	bleed = false,
	dense = false,
	grid = false,
	striped = false,
	noBorder = false,
	className,
	children,
	...props
}: { bleed?: boolean; dense?: boolean; grid?: boolean; striped?: boolean; noBorder?: boolean } & React.ComponentPropsWithoutRef<'div'>) {
	return (
		<TableContext.Provider value={{ bleed, dense, grid, striped } as React.ContextType<typeof TableContext>}>
			<div className="flow-root">
				<div {...props} className={cn(className, '-mx-[--gutter] overflow-x-auto whitespace-nowrap')}>
					<div className={cn('inline-block min-w-full align-middle', !bleed && 'sm:px-[--gutter]')}>
						<table className="min-w-full text-left text-sm/6">{children}</table>
					</div>
				</div>
			</div>
		</TableContext.Provider>
	);
}

export function TableHead({ className, ...props }: React.ComponentPropsWithoutRef<'thead'>) {
	return <thead className={cn(className, 'text-zinc-500 dark:text-zinc-400')} {...props} />;
}

export function TableBody(props: React.ComponentPropsWithoutRef<'tbody'>) {
	return <tbody {...props} />;
}

const TableRowContext = createContext<{ href?: string; target?: string; title?: string }>({
	href: undefined,
	target: undefined,
	title: undefined,
});

export function TableRow({
	href,
	target,
	title,
	className,
	children,
	...props
}: { href?: string; target?: string; title?: string } & React.ComponentPropsWithoutRef<'tr'>) {
	let { striped } = useContext(TableContext);

	return (
		<TableRowContext.Provider value={{ href, target, title } as React.ContextType<typeof TableRowContext>}>
			<tr
				{...props}
				className={cn(
					'transition-all',
					href &&
						'group has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/[2.5%]',
					striped && 'even:bg-zinc-950/[2.5%] dark:even:bg-white/[2.5%]',
					href && striped && 'hover:bg-zinc-950/5 dark:hover:bg-white/5',
					href && !striped && 'hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]',
					className,
				)}
			>
				{children}
			</tr>
		</TableRowContext.Provider>
	);
}

interface TableHeaderProps extends React.ComponentPropsWithoutRef<'th'> {
	centered?: boolean;
}

interface TableHeaderWithSortProps<T extends Record<string, any>> extends TableHeaderProps {
	sortKey: keyof T;
	searchParams?: T;
	searchKey?: string;
	searchValue?: string;
}

export function TableHeader<T extends Record<string, any>>(props: TableHeaderProps): React.ReactElement;
export function TableHeader<T extends Record<string, any>>(props: TableHeaderWithSortProps<T>): React.ReactElement;
export function TableHeader<T extends Record<string, any>>(props: TableHeaderProps | TableHeaderWithSortProps<T>) {
	let { bleed, grid } = useContext(TableContext);
	let { centered, children, className, sortKey, searchParams, searchKey, searchValue, ...headerProps } =
		props as TableHeaderWithSortProps<T>;

	const ascending = sortKey && searchParams?.[sortKey] === 'asc';
	const descending = sortKey && searchParams?.[sortKey] === 'desc';

	return (
		<th
			{...headerProps}
			className={cn(
				'border-b border-b-zinc-950/10 px-2 lg:px-4 py-1 lg:py-2 font-medium first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] dark:border-b-white/10',
				grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
				!bleed && 'sm:first:pl-2 sm:last:pr-2',
				centered && 'text-center',
				className,
			)}
		>
			{sortKey && searchParams ? (
				<a
					href={getTableSearchURL({
						param: sortKey,
						currentQuery: searchParams,
						searchKey,
						searchValue,
						value: ascending || descending ? 'desc' : 'asc',
					})}
					className="flex underline items-center gap-1 focus:outline-none hover:text-blue"
				>
					{children}
					{ascending && <ChevronUpIcon className="w-4 h-4" />}
					{descending && <ChevronDownIcon className="w-4 h-4" />}
				</a>
			) : (
				children
			)}
		</th>
	);
}

interface TableCellProps extends React.ComponentPropsWithoutRef<'td'> {
	centered?: boolean;
	to?: string;
}

export function TableCell({ className, centered, children, to, ...props }: TableCellProps) {
	let { bleed, dense, grid, striped, noBorder } = useContext(TableContext);
	let { href, target, title } = useContext(TableRowContext);
	let [cellRef, setCellRef] = useState<HTMLElement | null>(null);
	const link = to || href;

	return (
		<td
			ref={href ? setCellRef : undefined}
			{...props}
			className={cn(
				'relative px-2 lg:px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))]',
				!noBorder && 'border-b border-zinc-950/10',
				grid && 'border-l border-l-zinc-950/10 first:border-l-0',
				dense ? 'py-2' : 'py-4',
				!bleed && 'sm:first:pl-2 sm:last:pr-2',
				centered && 'text-center',
				link && 'underline',
				className,
			)}
		>
			{link && (
				<Link
					data-row-link
					to={link}
					target={target}
					aria-label={title}
					tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
					className="absolute inset-0 focus:outline-none"
				/>
			)}
			{children}
		</td>
	);
}

export function ViewHeader({ className, children, ...props }: TableHeaderProps) {
	return <TableHeader {...props} className={cn(className, 'w-24')} />;
}

interface ViewCellProps extends TableCellProps {
	text?: string;
}
export function ViewCell({ className, children, text = 'View', ...props }: ViewCellProps) {
	return (
		<TableCell {...props} className={cn(className, 'group-hover:text-blue w-24')}>
			<div className="flex items-center justify-end">
				<span className="hidden md:block">{text}</span>
				<ArrowRightIcon className="transition w-4 h-4 ml-1 group-hover:translate-x-1" />
			</div>
		</TableCell>
	);
}

export function ImageHeader({ className, children, ...props }: TableHeaderProps) {
	return <TableHeader {...props} className={cn(className, 'w-16')} />;
}

export function ImageCell({ className, children, ...props }: TableCellProps) {
	return (
		<TableCell {...props} className={cn('p-1 w-16', className)}>
			<div className="w-12 h-12 border border-blue rounded-xl overflow-hidden">
				<PatternImage containerClass="p-0.5">{children}</PatternImage>
			</div>
		</TableCell>
	);
}

export function EditCell({ className, children, ...props }: TableCellProps) {
	return (
		<TableCell {...props} className={cn(className, 'group-hover:text-blue')}>
			<div className="flex items-center justify-end">
				<span className="hidden md:block">Edit</span>
				<PencilIcon className="transition w-4 h-4 ml-1 group-hover:translate-x-1" />
			</div>
		</TableCell>
	);
}

interface StatusCellProps extends TableCellProps {
	status: StatusIconStatus;
}

export function StatusCell({ className, children, status, ...props }: StatusCellProps) {
	return (
		<TableCell {...props} className={className}>
			<div className="flex items-center gap-2 font-medium">
				<StatusIcon status={status} />
				<h6 className="text-xs">{capitaliseAndSpace(status)}</h6>
			</div>
		</TableCell>
	);
}

interface DownloadCellProps extends TableCellProps {
	text?: string;
}

export function DownloadCell({ className, children, text, ...props }: DownloadCellProps) {
	return (
		<TableCell {...props} className={cn(className, 'group-hover:text-blue')}>
			<div className="flex items-center justify-end">
				<span className="hidden md:block">{text || 'Download'}</span>
				<ArrowDownTrayIcon className="w-4 h-4 ml-1" />
			</div>
		</TableCell>
	);
}
